/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';

import {
  DeleteConfirmationModal,
  DropdownButton,
  Loader,
  Table,
} from '../../../Components/uielements';
import { getManagerialList, deleteManagerialList } from '../../../services/bioplus-services/user';
import { DeleteIcon } from '../../../Components/icons';
import { IntlMessages } from '../../../helper/langHelper';
import moment from 'moment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import { UserContainer } from '../UserV2/user.style';
import { useUserContext } from '../../../hooks/UserContext';

function extractData(dataArray, userTimeZone) {
  if (dataArray) {
    const extractedData = dataArray?.map((item) => {
      return {
        Name: `${item?.userId?.firstName ?? ''} ${item?.userId?.lastName ?? ''}`.trim(),
        email: item?.userId?.email,
        mobile: item?.userId?.phone,
        lastLogin: item?.userId?.lastLogin
          ? convertToUserTimeZoneDateTime(item?.userId?.lastLogin, userTimeZone)
          : 'N/A',
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const DistributorUserManagerial = () => {
  const userContext = useUserContext();
  const queryClient = useQueryClient();
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [deleteRole, setDeleteRole] = useState();

  const managerialQuery = useQuery(['distributorUserManagerial', page, pageSize], () =>
    getManagerialList({ page, pageSize, userType: 'PARTNER' })
  );

  const { userPermission } = userContext;
  const isDistributorAllowToManage = userPermission?.find(
    (permission) => permission?.label === 'distributor'
  );

  const canManage = !!isDistributorAllowToManage?.isManage;
  const canDownloadReport = !!isDistributorAllowToManage?.isReport;
  const isOwnAccount = userContext?.user?.email;

  const rawManagerial = managerialQuery?.data;
  const managerialTableData = Array.isArray(rawManagerial)
    ? rawManagerial
    : rawManagerial?.data ?? [];
  const total = Array.isArray(rawManagerial)
    ? managerialTableData.length
    : rawManagerial?.total ?? 0;

  const [allManagerial, setAllManagerial] = useState(undefined);
  useQuery(
    ['allDistributorUserManagerial'],
    () => getManagerialList({ userType: 'PARTNER', mode: 'ALL' }),
    {
      enabled: !allManagerial,
      onSuccess: (data) => {
        const rows = Array.isArray(data) ? data : data?.data ?? [];
        setAllManagerial(rows);
      },
    }
  );

  const handleNextPage = () => setPage((value) => value + 1);
  const handlePreviousPage = () => setPage((value) => value - 1);

  const deleteMutation = useMutation((roleId) => deleteManagerialList({ roleId }), {
    onSuccess: async () => {
      alert('Member deleted successfully.');
      await queryClient.invalidateQueries('distributorUserManagerial');
      await queryClient.invalidateQueries('allDistributorUserManagerial');
      setAllManagerial(undefined);
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message || 'Something went wrong.'}`);
    },
  });

  const handleDelete = (data) => {
    deleteMutation.mutate(data?._id);
    setDeleteRole(undefined);
  };

  // -------- Table columns --------
  const managerialColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        width: '25%',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName ?? '--'} ${
              cellData?.row?.original?.userId?.lastName ?? '--'
            }`}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'userId.email',
        width: '30%',
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'userId.phone',
        width: '20%',
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.userId?.lastLogin
              ? `${convertToUserTimeZoneDateTime(
                  cellData?.row?.original?.userId?.lastLogin,
                  userTimeZone
                )}`
              : 'N/A'}
          </TableCell>
        ),
      },
    ];

    if (canManage) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" defaultMessage="Actions" />,
        accessor: 'actions',
        isAction: true,
        width: '10%',
      });
    }

    return cols;
  }, [userTimeZone, canManage]);

  const actionList = [
    (data) => {
      const isDeleteDisabled = isOwnAccount === data?.userId?.email;
      return (
        <IconButton
          onClick={() => !isDeleteDisabled && setDeleteRole(data)}
          disabled={isDeleteDisabled}
          style={isDeleteDisabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
        >
          <DeleteIcon style={{ width: '19px', height: '19px' }} />
        </IconButton>
      );
    },
  ];

  const excelFormatData = extractData(allManagerial ?? [], userTimeZone);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (managerialQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="nav.distributors" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '15px' }}>
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
        </TwoColumns>
      </UserContainer>
      <TableWrapper>
        <Table
          data={managerialTableData}
          columns={managerialColumns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={total}
        />
      </TableWrapper>
      {deleteRole && (
        <DeleteConfirmationModal
          onClose={() => setDeleteRole(undefined)}
          message={
            <IntlMessages
              id="delete.comfirmation.text.user"
              values={{
                user: ` ${deleteRole?.userId?.firstName} ${deleteRole?.userId?.lastName}`,
              }}
            />
          }
          onConfirm={() => handleDelete(deleteRole)}
        />
      )}
    </StyledContainer>
  );
};

export default DistributorUserManagerial;
